function noop() {}

/**
 * Play one assembled daily edition through a single fenced media lifecycle.
 * The caller owns byte assembly, presentation state, and URL revocation. This
 * helper guarantees that no section boundary replaces the source or restarts
 * browser playback and that stale events cannot mutate a newer audio request.
 */
export async function startDailyDefaultEditionPlaylist({
  player,
  items,
  isActive,
  playbackRate = 1,
  createObjectUrl,
  replaceObjectUrl,
  clearPlayer,
  onLoading = noop,
  onPlaying = noop,
  onTimeline = noop,
  onFailed = noop,
  onFinished = noop,
}) {
  if (
    !player ||
    !Array.isArray(items) ||
    items.length === 0 ||
    typeof isActive !== "function" ||
    typeof createObjectUrl !== "function" ||
    typeof replaceObjectUrl !== "function" ||
    typeof clearPlayer !== "function"
  ) {
    throw new TypeError("daily_default_audio_playlist_runtime_invalid");
  }

  let currentObjectUrl = null;
  let settled = false;

  const resolvedPlaybackRate = () => {
    const value = typeof playbackRate === "function" ? playbackRate() : playbackRate;
    return Number.isFinite(value) && value > 0 ? value : 1;
  };

  const removeListeners = () => {
    listeners.forEach(([type, listener]) => player.removeEventListener(type, listener));
  };
  const active = () => !settled && isActive() === true;
  const abandonIfStale = () => {
    if (active()) return false;
    if (!settled) {
      settled = true;
      removeListeners();
    }
    return true;
  };
  const fail = () => {
    if (abandonIfStale()) return;
    settled = true;
    removeListeners();
    clearPlayer(currentObjectUrl);
    onFailed();
  };
  const finish = () => {
    if (abandonIfStale()) return;
    settled = true;
    removeListeners();
    clearPlayer(currentObjectUrl);
    onFinished();
  };
  const onEnded = () => {
    if (!currentObjectUrl || player.currentSrc !== currentObjectUrl || abandonIfStale()) return;
    finish();
  };
  const onError = () => {
    if (currentObjectUrl && player.currentSrc === currentObjectUrl) fail();
  };
  const timeline = (overrides) => {
    if (!abandonIfStale()) onTimeline(overrides);
  };
  const onPlayingEvent = () => timeline({ buffering: false, paused: false });
  const onPause = () => {
    if (!player.ended) timeline({ buffering: false, paused: true });
  };
  const onWaiting = () => timeline({ buffering: true });
  const onTimelineEvent = () => timeline({});
  const listeners = [
    ["loadedmetadata", onTimelineEvent],
    ["durationchange", onTimelineEvent],
    ["timeupdate", onTimelineEvent],
    ["progress", onTimelineEvent],
    ["seeked", onTimelineEvent],
    ["waiting", onWaiting],
    ["playing", onPlayingEvent],
    ["pause", onPause],
    ["ended", onEnded],
    ["error", onError],
  ];
  listeners.forEach(([type, listener]) => player.addEventListener(type, listener));
  try {
    if (abandonIfStale()) return;
    const nextObjectUrl = createObjectUrl(items);
    if (typeof nextObjectUrl !== "string" || !nextObjectUrl) {
      throw new TypeError("daily_default_audio_playlist_object_url_invalid");
    }
    if (abandonIfStale()) return;
    currentObjectUrl = nextObjectUrl;
    replaceObjectUrl(currentObjectUrl);
    onLoading(items[0], 0);
    if (abandonIfStale()) return;
    player.loop = false;
    player.src = currentObjectUrl;
    player.load();
    if (abandonIfStale()) return;
    player.playbackRate = resolvedPlaybackRate();
    await player.play();
    if (abandonIfStale()) return;
    onPlaying(items[0], 0);
  } catch {
    fail();
  }
}
